import { Capacitor } from '@capacitor/core';
import { Box, Button, Flex, Icon, Image, Link, Text, useColorModeValue, useToast, VStack } from '@chakra-ui/react';
import CustomButton from 'components/CustomButton';
import InputForm from 'components/InputForm';
import useAuth from 'contexts/useAuth';
import React, { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from '@google-recaptcha/react';
import { useForm } from 'react-hook-form';
import { FaWhatsapp } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getVariable } from 'whitelabel';
import ForgetPassword from './ForgetPassword';

interface LoginFormData {
  email: string;
  password: string;
  token?: string;
}

const MIN_PASSWORD_LENGTH = 8;

const LoginInputs: React.FC = () => {
  const { executeV3 } = useGoogleReCaptcha();
  const { control, handleSubmit, setValue } = useForm<LoginFormData>();
  const [show2fa, setShow2fa] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const isNative = Capacitor.isNativePlatform();

  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleReCaptchaVerify = useCallback(
    async (type = 'CUSTOMER_LOGIN') => {
      if (!executeV3) {
        console.log('Execute recaptcha not yet available');
        return;
      }
      return await executeV3(type);
    },
    [executeV3],
  );

  const showErrorToast = (message: string) => {
    toast({
      title: 'Error',
      description: message,
      status: 'error',
      duration: 5000,
      position: 'top',
      isClosable: true,
    });
  };

  const handleLogin = async (dataLogin: LoginFormData) => {
    if (dataLogin.password.length < MIN_PASSWORD_LENGTH) {
      showErrorToast('A senha deve ter no mínimo 8 caracteres');
      return;
    }

    setLoading(true);
    const captchaKey = await handleReCaptchaVerify();

    try {
      await signIn({
        username: dataLogin.email,
        token: dataLogin.token,
        password: dataLogin.password,
        history: null,
        captchaKey,
      });
      localStorage.setItem('email_credential', dataLogin.email);
    } catch (error: any) {
      if (error.response?.status === 412) {
        setShow2fa(true);
      }
      showErrorToast(error?.response?.data?.message || 'Erro ao realizar login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const savedLogin = localStorage.getItem('email_credential');
    if (savedLogin) {
      setValue('email', savedLogin);
      passwordRef.current?.focus();
    }
  }, [setValue]);

  return (
    <Box
      w="100%"
      maxW="420px"
      mx="auto"
      p={8}
      bg={cardBg}
      borderRadius="xl"
      boxShadow="xl"
      borderWidth="1px"
      borderColor={borderColor}
    >
      <Box
        w="full"
        alignItems="center"
        display="flex"
        mb={6}
        flexDirection="column"
      >
        <Image
          src={getVariable('logo')}
          width="200px"
        />
      </Box>

      <Text
        fontSize="2xl"
        fontWeight="bold"
        mb={6}
        textAlign="center"
        color={textColor}
      >
        Bem-vindo de volta
      </Text>
      <form onSubmit={handleSubmit(handleLogin)}>
        <VStack spacing={6}>
          <InputForm
            control={control}
            label="E-mail"
            name="email"
            placeholder="seu@e-mail.com"
            beatify
            type="email"
            variant="filled"
          />
          <InputForm
            ref={passwordRef}
            control={control}
            label="Senha"
            placeholder="*******"
            name="password"
            beatify
            type="password"
            variant="filled"
          />
          {show2fa && (
            <InputForm
              control={control}
              label="2FA"
              placeholder="Caso ativo, digite o 2fa"
              name="token"
              beatify
              required={false}
              isRequired={false}
              rules={{ required: false }}
              type="text"
              variant="filled"
            />
          )}
          <Button
            fontSize="md"
            loadingText="Entrando..."
            isLoading={loading}
            variant="solid"
            colorScheme="brand"
            fontWeight="bold"
            w="100%"
            h="50px"
            mb="24px"
            type="submit"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: 'lg',
            }}
            transition="all 0.2s"
          >
            Entrar
          </Button>
        </VStack>
        <Flex
          justifyContent="center"
          align="center"
          mb="24px"
        >
          <ForgetPassword />
        </Flex>
      </form>
      {isNative && (
        <Box mb={4}>
          <CustomButton
            variant="outline"
            onClick={() => navigate('/auth/register')}
            w="100%"
          >
            Criar conta
          </CustomButton>
        </Box>
      )}
      <Link
        fontSize="sm"
        mt={5}
        variant="link"
        size="sm"
        fontWeight="500"
        w="100%"
        mb="10px"
        href={getVariable('supportWhatsapp')}
        target="_blank"
        referrerPolicy="no-referrer"
        textAlign="center"
        verticalAlign="middle"
        color="brand.500"
        _hover={{
          textDecoration: 'none',
          color: 'brand.600',
        }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={2}
      >
        <Icon
          as={FaWhatsapp}
          boxSize={5}
        />
        Precisa de ajuda?
      </Link>
    </Box>
  );
};

export default LoginInputs;
